import Data from 'data';
import StatObject from 'logic/statObject';
import { d2_addpercent, d2_percent, missileCalc, missileFormula, skillCalc, skillFormula } from './calc';

export class DamageCalculator extends StatObject {
  constructor(stats, weapon, missile, tohit, damagerate) {
    super();
    Object.defineProperty(this, '_stats', {value: stats});
    Object.defineProperty(this, '_weapon', {value: weapon});
    Object.defineProperty(this, '_missile', {value: missile});
    Object.defineProperty(this, '_tohit', {value: tohit});
    Object.defineProperty(this, '_convert', {value: {}});
    Object.defineProperty(this, '_damagerate', {value: damagerate});
  }

  finisher() {
    const values = this._stats.base.progressive;
    if (values) {
      const sources = this._stats.base.sources;
      for (const id in values) {
        switch (id) {
        case 'convert_fire': this.convert('fire', values[id]); break;
        case 'convert_cold': this.convert('cold', values[id]); break;
        case 'convert_ltng': this.convert('ltng', values[id]); break;
        case 'convert_mag': this.convert('mag', values[id]); break;
        default:    
          this.add(id, values[id], 0, 0, sources[`progressive#${id}`]);
        }
      }
    }
  }

  elemental(type, min, max, len) {
    switch (type) {
    case 'fire':
      this.skillbonus('firemindam', min);
      this.skillbonus('firemaxdam', max);
      break;
    case 'cold':
      this.skillbonus('coldmindam', min);
      this.skillbonus('coldmaxdam', max);
      this.skillbonus('coldlength', max);
      break;
    case 'ltng':
      this.skillbonus('lightmindam', min);
      this.skillbonus('lightmaxdam', max);
      break;
    case 'pois':
      this.skillbonus('poisonmindam', min);
      this.skillbonus('poisonmaxdam', max);
      this.skillbonus('poisonlength', len);
      break;
    case 'mag':
      this.skillbonus('magicmindam', min);
      this.skillbonus('magicmaxdam', max);
      break;
    // no default
    }
  }

  physical(min, max) {
    this.skillbonus('mindamage', min);
    this.skillbonus('maxdamage', max);
  }

  _weaponelem(etype) {
    const weapon = this._weapon;
    const stats = this._stats.base;
    if (!etype || etype === 'fire') {
      if (!this._missile && stats.passive_fire_mastery) {
        this.add_from('firemindam', weapon, 'firemindam', value => d2_addpercent(value << 8, stats.passive_fire_mastery));
        this.add_from('firemaxdam', weapon, 'firemaxdam', value => d2_addpercent(value << 8, stats.passive_fire_mastery));
      } else {
        this.add_from('firemindam', weapon, 'firemindam', value => value << 8);
        this.add_from('firemaxdam', weapon, 'firemaxdam', value => value << 8);
      }
    }
    if (!etype || etype === 'cold') {
      if (!this._missile && stats.passive_cold_mastery) {
        this.add_from('coldmindam', weapon, 'coldmindam', value => d2_addpercent(value << 8, stats.passive_cold_mastery));
        this.add_from('coldmaxdam', weapon, 'coldmaxdam', value => d2_addpercent(value << 8, stats.passive_cold_mastery));
      } else {
        this.add_from('coldmindam', weapon, 'coldmindam', value => value << 8);
        this.add_from('coldmaxdam', weapon, 'coldmaxdam', value => value << 8);
      }
      this.add_from('coldlength', weapon, 'coldlength');
    }
    if (!etype || etype === 'ltng') {
      if (!this._missile && stats.passive_ltng_mastery) {
        this.add_from('lightmindam', weapon, 'lightmindam', value => d2_addpercent(value << 8, stats.passive_ltng_mastery));
        this.add_from('lightmaxdam', weapon, 'lightmaxdam', value => d2_addpercent(value << 8, stats.passive_ltng_mastery));
      } else {
        this.add_from('lightmindam', weapon, 'lightmindam', value => value << 8);
        this.add_from('lightmaxdam', weapon, 'lightmaxdam', value => value << 8);
      }
    }
    if (!etype || etype === 'pois') {
      if (!this._missile && stats.passive_pois_mastery) {
        this.add_from('poisonmindam', weapon, 'poisonmindam', value => d2_addpercent(value, stats.passive_pois_mastery));
        this.add_from('poisonmaxdam', weapon, 'poisonmaxdam', value => d2_addpercent(value, stats.passive_pois_mastery));
      } else {
        this.add_from('poisonmindam', weapon, 'poisonmindam');
        this.add_from('poisonmaxdam', weapon, 'poisonmaxdam');
      }
      this.add_from('poisonlength', weapon, 'poisonlength');
      this.add_from('poison_count', weapon, 'poison_count');
    }
    if (!etype || etype === 'mag') {
      if (!this._missile && stats.passive_mag_mastery) {
        this.add_from('magicmindam', weapon, 'magicmindam', value => d2_addpercent(value << 8, stats.passive_mag_mastery));
        this.add_from('magicmaxdam', weapon, 'magicmaxdam', value => d2_addpercent(value << 8, stats.passive_mag_mastery));
      } else {
        this.add_from('magicmindam', weapon, 'magicmindam', value => value << 8);
        this.add_from('magicmaxdam', weapon, 'magicmaxdam', value => value << 8);
      }
    }
  }

  srcdam(srcdam, half2h, etype, nopct) {
    if (!srcdam) return;
    if (!etype) {
      Object.defineProperty(this, '_srcdam', {value: srcdam});
    }

    const weapon = this._weapon;

    if (!etype) {
      if (this._missile && weapon.item_throw_maxdamage) {
        this.add_from('mindamage', weapon, 'item_throw_mindamage', value => value << 8);
        this.add_from('maxdamage', weapon, 'item_throw_maxdamage', value => value << 8);
      } else {
        this.add_from('mindamage', weapon, 'mindamage', value => value << 8);
        this.add_from('maxdamage', weapon, 'maxdamage', value => value << 8);
      }
      if (!this._missile && weapon.item_normaldamage) {
        this.add_from('mindamage', weapon, 'item_normaldamage', value => value << 8);
        this.add_from('maxdamage', weapon, 'item_normaldamage', value => value << 8);
      }
    }

    this._weaponelem(etype);

    if (half2h && weapon.twohanded) {
      srcdam >>= 1;
    }

    const factor = srcdam / 128;
    this.multiply('mindamage', factor);
    this.multiply('maxdamage', factor);
    this.multiply('firemindam', factor);
    this.multiply('firemaxdam', factor);
    this.multiply('coldmindam', factor);
    this.multiply('coldmaxdam', factor);
    this.multiply('coldlength', factor);
    this.multiply('lightmindam', factor);
    this.multiply('lightmaxdam', factor);
    this.multiply('poisonmindam', factor);
    this.multiply('poisonmaxdam', factor);
    this.multiply('poison_count', factor); // this is actually accurate to the game...
    this.multiply('magicmindam', factor);
    this.multiply('magicmaxdam', factor);

    if (!nopct) {
      this.add_from('damagepercent', weapon, 'damagepercent');
      if (this._missile) {
        this.add_from('damagepercent', weapon, 'passive_mastery_throw_dmg');
      } else {
        this.add_from('damagepercent', weapon, 'passive_mastery_melee_dmg');
      }
    }

    if (!this.nocrit) {
      this.add('item_undeaddamage_percent', weapon.item_undeaddamage_percent, 0, 0, 'weapon');
      this.add('item_demondamage_percent', weapon.item_demondamage_percent, 0, 0, 'weapon');
    }
  }

  kickdamage() {
    Object.defineProperty(this, '_srcdam', {value: 128});

    const stats = this._stats.base;
    const item = Data.items[this._stats.bootbase];

    Object.defineProperty(this, 'kick', {value: true});
    const strdex = (stats.strength || 0) + (stats.dexterity || 0) - 20;
    this.base_mindamage = d2_addpercent(strdex / 4 | 0, this.damagepercent || 0) << 8;
    this.base_maxdamage = d2_addpercent(strdex / 3 | 0, this.damagepercent || 0) << 8;

    if (item) {
      this.add('mindamage', (item.mindam || 0) << 8, 0, 0, this._stats.bootslot);
      this.add('maxdamage', (item.maxdam || 0) << 8, 0, 0, this._stats.bootslot);
    }

    this._weaponelem();

    this.add_from('damagepercent', this._weapon, 'damagepercent');
    this.add('damagepercent', 120 * (stats.strength || 0) / 100 | 0, 0, 0, 'stats');
  }

  smitedamage() {
    const stats = this._stats.base;
    const item = Data.items[this._stats.shieldbase];
    if (!item) return;

    Object.defineProperty(this, '_srcdam', {value: 128});

    Object.defineProperty(this, 'smite', {value: true});

    this.add('mindamage', (item.mindam || 0) << 8, 0, 0, this._stats.shieldslot);
    this.add('maxdamage', (item.maxdam || 0) << 8, 0, 0, this._stats.shieldslot);
    this.add_from('mindamage', stats, 'smite_mindamage', value => value << 8);
    this.add_from('maxdamage', stats, 'smite_maxdamage', value => value << 8);
    if (this._weapon.item_normaldamage) {
      this.add_from('mindamage', this._weapon, 'item_normaldamage', value => value << 8);
      this.add_from('maxdamage', this._weapon, 'item_normaldamage', value => value << 8);
    }

    this.add_from('damagepercent', this._weapon, 'damagepercent');
    this.add('damagepercent', stats.strength, 0, 0, 'stats');
  }

  skillbonus(id, value) {
    if (Array.isArray(value)) {
      this.add(id, value[0], 0, 0, value[1]);
    } else {
      this.add(id, value, 0, 0, 'skill');
    }
  }

  convert(elem, percent) {
    if (!elem || !percent) return;
    this._convert[elem] = (this._convert[elem] || 0) + percent;
  }

  empty() {
    return !(this.mindamage || this.maxdamage || this.firemindam || this.firemaxdam || this.coldmindam || this.coldmaxdam ||
      this.lightmindam || this.lightmaxdam || (this.poisonlength && (this.poisonmindam || this.poisonmaxdam)) || this.magicmindam || this.magicmaxdam);
  }

  duration(frames) {
    if (this.frames == null) {
      Object.defineProperty(this, 'frames', {value: frames, writable: true});
    } else {
      this.frames = frames;
    }
    return this;
  }

  calculate(target, nonegative, noblock) {
    if (target.undead && this.item_undeaddamage_percent) {
      this.add('damagepercent', this.item_undeaddamage_percent, 0, 0, 'targetdamage');
    }
    if (target.demon && this.item_demondamage_percent) {
      this.add('damagepercent', this.item_demondamage_percent, 0, 0, 'targetdamage');
    }
    if (target.undead && this.skill_undeaddamage_percent) {
      this._addSource('skill_physdamage', this.skill_undeaddamage_percent, 'targetdamage');
      this._addSource('skill_firedamage', this.skill_undeaddamage_percent, 'targetdamage');
      this._addSource('skill_colddamage', this.skill_undeaddamage_percent, 'targetdamage');
      this._addSource('skill_lightdamage', this.skill_undeaddamage_percent, 'targetdamage');
      this._addSource('skill_poisondamage', this.skill_undeaddamage_percent, 'targetdamage');
      this._addSource('skill_magicdamage', this.skill_undeaddamage_percent, 'targetdamage');
    }
    if (target.demon && this.skill_demondamage_percent) {
      this._addSource('skill_physdamage', this.skill_demondamage_percent, 'targetdamage');
      this._addSource('skill_firedamage', this.skill_demondamage_percent, 'targetdamage');
      this._addSource('skill_colddamage', this.skill_demondamage_percent, 'targetdamage');
      this._addSource('skill_lightdamage', this.skill_demondamage_percent, 'targetdamage');
      this._addSource('skill_poisondamage', this.skill_demondamage_percent, 'targetdamage');
      this._addSource('skill_magicdamage', this.skill_demondamage_percent, 'targetdamage');
    }
    this.final_mindamage = d2_addpercent(this.mindamage || 0, this.damagepercent || 0) + (this.base_mindamage || 0);
    this.final_maxdamage = d2_addpercent(this.maxdamage || 0, this.damagepercent || 0) + (this.base_maxdamage || 0);

    const orig_mindamage = this.final_mindamage, orig_maxdamage = this.final_maxdamage;
    this.conversion = 0;
    const convert = (type, percent) => {
      const min = d2_percent(orig_mindamage, percent);
      const max = d2_percent(orig_maxdamage, percent);
      this.add(`${type}mindam`, min, 0, 0, 'convert');
      this.add(`${type}maxdam`, max, 0, 0, 'convert');
      this.final_mindamage = Math.max(0, this.final_mindamage - min);
      this.final_maxdamage = Math.max(0, this.final_maxdamage - max);
      this.conversion = Math.min(100, this.conversion + percent);
    };
    if (this._convert.fire) convert('fire', this._convert.fire);
    if (this._convert.cold) convert('cold', this._convert.cold);
    if (this._convert.ltng) convert('light', this._convert.ltng);
    if (this._convert.mag) convert('magic', this._convert.mag);

    const stats = this._stats.base;
    const tstats = target.base;

    this.display_mindamage = 0;
    this.display_maxdamage = 0;

    const rate = this._damagerate || 1024;
    const add = (name, min, max, dr, res, abs_pct, abs) => {
      if (res >= 100) return 0;
      const calc = value => {
        if (this[`skill_${name}damage`]) value = d2_addpercent(value, this[`skill_${name}damage`]);
        if (target.undead && this.skill_undeaddamage_percent) value = d2_addpercent(value, this.skill_undeaddamage_percent);
        if (target.demon && this.skill_demondamage_percent) value = d2_addpercent(value, this.skill_demondamage_percent);
        if (dr) value = Math.max(0, value - (dr * rate / 4 | 0));
        if (res) value = d2_addpercent(value, -res);
        if (this._missile && stats.skill_missile_damage_scale) value = d2_percent(value, stats.skill_missile_damage_scale);
        if (abs_pct || abs) {
          const total = Math.min(d2_percent(value, abs_pct || 0) + ((abs || 0) * rate / 4 | 0), value * 40 / 100 | 0);
          value -= total * 2;
        }
        return value;//Math.round(value / 256);
      };
      min = calc(min || 0);
      max = calc(max || 0);
      if (name === 'phys' && !this._missile && min) {
        max = Math.max(max, min + 256);
      } else if (min > max) {
        const tmp = min;
        min = max;
        max = tmp;
      }
      this[`applied_${name}mindam`] = min;
      this[`applied_${name}maxdam`] = max;
      if (this.frames) {
        const factor = 25 / 256 / (this.frames < 0 ? -this.frames : 1);
        min *= factor;
        max *= factor;
      } else {
        if (this.ticks) {
          min *= this.ticks;
          max *= this.ticks;
        }
        min = Math.round(min / 256);
        max = Math.round(max / 256);
      }
      this.display_mindamage += (this[`display_${name}mindam`] = min);
      this.display_maxdamage += (this[`display_${name}maxdam`] = max);
    };

    function get_resist(name, pierce, cap) {
      let value = tstats[name] || 0;
      const immunity = value >= 100;
      if (value >= 100 && pierce) {
        value = 95;
      }
      let reduce = tstats[`negative_${name}`] || 0;
      if (immunity && target.unit !== 'player' && target.unit !== 'mercenary') {
        reduce = (reduce / 5) | 0;
      }
      value -= reduce;
      if (target.unit === 'player' || target.unit === 'mercenary') {
        value = Math.min(value, cap);
      }
      return value;
    }

    const damageresist = (target.undead && stats.skill_bypass_undead ? 0 : get_resist('damageresist', stats.item_pierce_damage_immunity, 50));
    function pierceResist(base=0, pierce=0) {
      if (base >= 100) return base;
      if (base < 0 && nonegative) {
        base = Math.max(0, 100 + base);
        return -Math.round(Math.min(Math.min(100, pierce || 0), base) * Math.sqrt(base / 100));
      } else {
        return Math.max(-100, base - (pierce || 0));
      }
    }
    function pierceResistNew(name, sunder, pierce, pierceReduce) {
      const immunity = (tstats[name] || 0) >= 100;
      if (immunity && pierceReduce) {
        pierce = (pierce - pierceReduce) + ((pierceReduce / 5) | 0);
      }
      return pierceResist(get_resist(name, sunder), pierce);
    }
    const fireresist = pierceResistNew('fireresist', stats.item_pierce_fire_immunity, stats.passive_fire_pierce, stats.passive_fire_pierce_reduce);
    const coldresist = pierceResistNew('coldresist', stats.item_pierce_cold_immunity, stats.passive_cold_pierce, stats.passive_cold_pierce_reduce);
    const lightresist = pierceResistNew('lightresist', stats.item_pierce_light_immunity, stats.passive_ltng_pierce, stats.passive_ltng_pierce_reduce);
    const magicresist = pierceResistNew('magicresist', stats.item_pierce_magic_immunity, stats.passive_mag_pierce, stats.passive_mag_pierce_reduce);

    add('phys', this.final_mindamage, this.final_maxdamage, tstats.normal_damage_reduction, damageresist);
    add('fire', this.firemindam, this.firemaxdam, tstats.magic_damage_reduction, fireresist, tstats.item_absorbfire_percent, tstats.item_absorbfire);
    add('cold', this.coldmindam, this.coldmaxdam, tstats.magic_damage_reduction, coldresist, tstats.item_absorbcold_percent, tstats.item_absorbcold);
    add('light', this.lightmindam, this.lightmaxdam, tstats.magic_damage_reduction, lightresist, tstats.item_absorblight_percent, tstats.item_absorblight);
    add('magic', this.magicmindam, this.magicmaxdam, tstats.magic_damage_reduction, magicresist, tstats.item_absorbmagic_percent, tstats.item_absorbmagic);

    const poisonresist = pierceResistNew('poisonresist', stats.item_pierce_poison_immunity, stats.passive_pois_pierce, stats.passive_pois_pierce_reduce);
    const poisonlength = d2_addpercent((this.poisonlength || 0) / (this.poison_count || 1), -(target.base.item_poisonlengthresist || 0));
    if (poisonlength && poisonresist < 100) {
      this.applied_poisonmindam = d2_addpercent(this.poisonmindam, -poisonresist);
      this.applied_poisonmaxdam = d2_addpercent(this.poisonmaxdam, -poisonresist);
      this.display_mindamage += (this.display_poisonmindam = Math.round(this.applied_poisonmindam * poisonlength / 256));
      this.display_maxdamage += (this.display_poisonmaxdam = Math.round(this.applied_poisonmaxdam * poisonlength / 256));
      this.display_poisonlength = poisonlength;
    }

    this.display_physresist = damageresist;
    this.display_fireresist = fireresist;
    this.display_coldresist = coldresist;
    this.display_lightresist = lightresist;
    this.display_magicresist = magicresist;
    this.display_poisonresist = poisonresist;

    if (this._tohit) {
      const weapon = this._weapon;
      this.add_from('tohit', weapon, 'tohit');
      this.add_from('tohit_shrine', stats, 'shrine_tohit_percent');
      this.add_from('tohit_percent', weapon, 'item_tohit_percent');
      if (this._missile) {
        this.add_from('tohit_percent', weapon, 'passive_mastery_throw_th');
      } else {
        this.add_from('tohit_percent', weapon, 'passive_mastery_melee_th');
      }
      const ar = this.display_tohit = d2_addpercent(d2_addpercent(this.tohit || 0, this.tohit_percent || 0), this.tohit_shrine || 0);

      if (weapon.item_ignoretargetac && (target.type === 'normal' || target.type === 'dummy')) {
        this.display_armorclass_percent = 100;
      } if (target.type === 'super' || target.type === 'boss' || target.unit === 'player' || target.unit === 'mercenary') {
        this.add_from('display_armorclass_percent', weapon, 'item_fractionaltargetac', value => value / 2 | 0);
      } else {
        this.add_from('display_armorclass_percent', weapon, 'item_fractionaltargetac');
      }
      const ac = this.display_armorclass = d2_addpercent(tstats.armorclass || 0, -(this.display_armorclass_percent || 0));

      const level = this._stats.level || 0;
      this.display_hitchance = Math.max(5, Math.min(95, 200 * ar / (ar + ac) * level / (level + (target.level || 0))));
    }
    if (!noblock && !this.smite) {
      if (target.blockchance && (this._missile ? this.final_maxdamage : this._srcdam)) {
        this.display_blockchance = target.blockchance;
      } else if (tstats.passive_weaponblock) {
        this.display_blockchance = tstats.passive_weaponblock;
      }
    }

    return this;
  }
}

export function missileDamageCalc(missile, stats, weapon, level, extra, target) {
  if (!missile) return null;

  const skill = Data.skillByName[missile.skill?.toLowerCase()];
  const damageSource = skill || missile;
  const damageFormula = skill ? (id => skillFormula(skill, id, stats, level)) : (id => missileFormula(missile, id, stats, level));

  const mastery = value => {
    if (!skill && !missile.applymastery) return value;
    const pct = stats.base?.[`passive_${damageSource.etype}_mastery`];
    if (!pct) return value;
    return d2_addpercent(value, pct);
  };

  const result = new DamageCalculator(stats, weapon, true, !!(extra?.tohit != null ? extra.tohit : missile.tohit));

  if (damageSource.etype) {
    result.elemental(damageSource.etype, mastery(damageFormula('edns')), mastery(damageFormula('edxs')), damageFormula('edln'));
  }

  if (missile.srcdam !== -1) {
    result.srcdam(damageSource.srcdam, missile.half2hsrc, extra?.elementOnly ? (skill?.etype || missile.etype) : null);
  }

  result.physical(damageFormula('pdns'), damageFormula('pdxs'));

  result.resultflags = damageSource.resultflags;

  switch (missile.psrvhitfunc) {
  case 7: // holy bolt
    if (target) {
      switch (missile.shitpar2) {
      case 1:
        if (!target.undead) return;
        break;
      case 2:
        if (!target.demon) return;
        break;
      case 3:
        if (!target.demon && !target.undead) return;
        break;
      // no default
      }
    }
    break;
  }


  switch (missile.psrvdmgfunc) {
  case 1:
  case 12:
    result.convert(missile.etype, missileCalc(missile, missile.dmgcalc1, stats, level));
    break;
  case 2:
  case 4:
    result.add_func('freezelength', 'coldlength', value => value * (missile.dparam1 || 0) / 100 | 0);
    result.coldlength = 0;
    break;
  case 5:
    result.skillbonus('skill_undeaddamage_percent', missile.dparam1);
    result.skillbonus('skill_demondamage_percent', missile.dparam2);
    break;
  case 7:
    result.skillbonus('stunlength', missile.dparam1 || skillFormula(skill, 'ln12', stats, level));
    break;
  case 9:
    result.skillbonus('stunlength', missile.dparam1 || skill?.param2);
    break;
  // no default
  }

  if (missile.collidetype === 8) {
    result.duration(missileFormula(missile, 'rang', stats, level) - 1);
  }

  return result.empty() ? null : result;
}

export function missileDamage(missile, stats, weapon, level, target, extra) {
  if (!missile) return null;

  const submissile = Data.missileByName[missile.submissile1];
  const hitsubmissile = Data.missileByName[missile.hitsubmissile1];

  const submissileDamage = (missile, localExtra) => {
    if (localExtra && extra) {
      Object.assign(localExtra, extra);
    } else if (!localExtra) {
      localExtra = extra;
    }
    const damage = missileDamageCalc(missile, stats, weapon, level, localExtra, target);
    if (damage && localExtra) {
      if (localExtra.frames) damage.duration(localExtra.frames);
      if (localExtra.ticks) damage.ticks = localExtra.ticks;
      if (localExtra.radius) damage.radius = localExtra.radius;
      if (localExtra.stats) {
        for (const id in localExtra.stats) {
          damage.skillbonus(id, localExtra.stats[id]);
        }
      }
    }
    return damage;
  };

  switch (missile.psrvdofunc) {
  case 2: // poison javelin
    return {
      missile: submissileDamage(missile)?.calculate(target),
      trail: submissileDamage(submissile)?.calculate(target),
    };
  case 10: // blizzard
    return {
      shard: submissileDamage(submissile)?.calculate(target),
    };
  case 12: // diabwall
  case 23: // firestorm
    return {
      damage: submissileDamage(submissile)?.duration(missileFormula(submissile, 'rang', stats, level) - 1).calculate(target),
    };
  case 25: // fissure
    return {
      crack: submissileDamage(submissile)?.calculate(target),
    };
  case 26: // plague vines
    return {
      damage: submissileDamage(submissile)?.calculate(target),
    };
  case 31: // wake of fire sentry
    return {
      damage: submissileDamage(submissile)?.calculate(target),
    };
  // no default
  }
  switch (missile.psrvhitfunc) {
  case 2: // plague javelin
    return {
      missile: missile.collidetype !== 6 && submissileDamage(missile)?.calculate(target),
      cloud: submissileDamage(hitsubmissile)?.calculate(target),
    };
  case 4: // exploding arrow
  {
    const {damage, ...other} = missileDamage(hitsubmissile, stats, weapon, level, target, {...extra, elementOnly: true});
    return {
      missile: submissileDamage(missile)?.calculate(target),
      explosion: damage,
      ...other,
    };
  }
  case 9: // immolation arrow
  {
    const duration = missileCalc(missile, missile.shitcalc1, stats, level);
    const radius = missile.shitpar1 || skillCalc(Data.skills[missile.skill?.toLowerCase()], 'clc1', stats, level);
    return {
      missile: submissileDamage(missile)?.calculate(target),
      explosion: submissileDamage(missile, {elementOnly: true, tohit: false})?.calculate(target),
      burn: submissileDamage(hitsubmissile, {frames: duration - 1, radius})?.calculate(target),
    };
  }
  case 47: // molten boulder
    return {
      missile: submissileDamage(missile)?.calculate(target),
      burn: submissileDamage(hitsubmissile)?.duration(missileFormula(hitsubmissile, 'rang', stats, level) - 1).calculate(target),
    };
  case 14: // meteor
    return {
      damage: submissileDamage(missile)?.calculate(target),
      burn: submissileDamage(hitsubmissile)?.duration(extra?.fireFrames || 0).calculate(target),
    };
  case 20: // lightning fury
    return {
      missile: submissileDamage(missile)?.calculate(target),
      bolt: submissileDamage(hitsubmissile)?.calculate(target),
    };
  case 22: // fist of the heavens
    return {
      missile: submissileDamage(missile)?.calculate(target),
      bolt: submissileDamage(hitsubmissile)?.calculate(target),
    };
  case 29: // frozen orb
    return {
      missile: submissileDamage(missile)?.calculate(target),
      bolt: submissileDamage(submissile)?.calculate(target),
      nova: submissileDamage(hitsubmissile)?.calculate(target),
    };
  case 36: // bomb in air (fire blast)
  case 48: // molten boulder (emerge)
    return missileDamage(hitsubmissile, stats, weapon, level, target, extra);
  case 56: // armageddon
    return {
      damage: submissileDamage(missile)?.calculate(target, false, true),
      burn: submissileDamage(hitsubmissile)?.duration(missileFormula(hitsubmissile, 'rang', stats, level) - 1).calculate(target),
    };
  default:
    return {damage: submissileDamage(missile)?.calculate(target)};
  }
}
